// components/PromoAnalyticsPanel.tsx
// Аналитика промо-рассылки «Промо 14 дней» — только для superadmin
import React, { useEffect, useState } from 'react';
import api from '../api/client';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useToast } from '../contexts/ToastContext';

interface PromoItem {
  _id: string;
  name?: string;
  email: string;
  company?: string;
  promoEmailSentAt?: string;
  promoLinkClickedAt?: string;
  promoActivatedAt?: string;
  promoEndsAt?: string;
  lastEmailTemplateName?: string;
  status: 'sent-not-clicked' | 'clicked-not-activated' | 'active' | 'expired' | 'not-activated';
}

interface PromoStats {
  totalSent: number;
  totalClicked: number;
  totalActivated: number;
  totalActive: number;
  totalExpired: number;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  'sent-not-clicked':    { label: '📩 Отправлено, не кликнул',  color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
  'clicked-not-activated':{ label: '👀 Кликнул, не активировал', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  'active':              { label: '✅ Активен',                  color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  'expired':             { label: '⏰ Истёк',                    color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  'not-activated':       { label: '— Не активирован',            color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
};

const fmtDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PromoAnalyticsPanel: React.FC = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState<PromoItem[]>([]);
  const [stats, setStats] = useState<PromoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/promo/analytics');
      setItems(res.data.items || []);
      setStats(res.data.stats || null);
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  
  const handleExportExcel = () => {
    if (!items.length) return;
    const ws = XLSX.utils.json_to_sheet(items.map(i => ({
      'Имя': i.name || '—',
      'Email': i.email,
      'Компания': i.company || '—',
      'Письмо': fmtDate(i.promoEmailSentAt),
      'Клик': fmtDate(i.promoLinkClickedAt),
      'Активация': fmtDate(i.promoActivatedAt),
      'Окончание': fmtDate(i.promoEndsAt),
      'Статус': STATUS_LABELS[i.status]?.label || i.status,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Промо 14 дней');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `promo_stats_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

return (
    <div>
      {/* ═══ Статистика ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Card label="📩 Отправлено" value={stats?.totalSent || 0} color="#3b82f6" />
        <Card label="👀 Кликнули" value={stats?.totalClicked || 0} color="#f59e0b" sub={stats?.totalSent ? `${Math.round(stats.totalClicked / stats.totalSent * 100)}%` : ''} />
        <Card label="🎁 Активировали" value={stats?.totalActivated || 0} color="#10b981" sub={stats?.totalSent ? `${Math.round(stats.totalActivated / stats.totalSent * 100)}%` : ''} />
        <Card label="✅ Активны сейчас" value={stats?.totalActive || 0} color="#8b5cf6" />
        <Card label="⏰ Истекли" value={stats?.totalExpired || 0} color="#ef4444" />
      </div>

      {/* ═══ Фильтры ═══ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')} label="Все" count={items.length} />
        <FilterBtn active={filter === 'sent-not-clicked'} onClick={() => setFilter('sent-not-clicked')} label="📩 Не кликнули" count={items.filter(i => i.status === 'sent-not-clicked').length} />
        <FilterBtn active={filter === 'clicked-not-activated'} onClick={() => setFilter('clicked-not-activated')} label="👀 Кликнули, не активировали" count={items.filter(i => i.status === 'clicked-not-activated').length} />
        <FilterBtn active={filter === 'active'} onClick={() => setFilter('active')} label="✅ Активны" count={items.filter(i => i.status === 'active').length} />
        <FilterBtn active={filter === 'expired'} onClick={() => setFilter('expired')} label="⏰ Истекли" count={items.filter(i => i.status === 'expired').length} />
      </div>

      {/* ═══ Таблица ═══ */}
      {loading ? (
        <p style={{ textAlign: 'center', padding: 40, color: '#8b8fa8' }}>Загрузка...</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.6)', borderRadius: 16, border: '1px dashed rgba(102,126,234,0.25)' }}>
          <div style={{ fontSize: 40 }}>🎁</div>
          <p style={{ color: '#8b8fa8', marginTop: 12 }}>Нет пользователей с промо-рассылкой</p>
          <p style={{ color: '#94a3b8', fontSize: 12 }}>Отправьте «Промо 14 дней» через раздел «Рассылки»</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: 'white', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <button onClick={handleExportExcel} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>📊 Экспорт в Excel</button><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>Email / Имя</th>
                <th style={thStyle}>Компания</th>
                <th style={thStyle}>📩 Письмо</th>
                <th style={thStyle}>👀 Клик</th>
                <th style={thStyle}>🎁 Активация</th>
                <th style={thStyle}>⏰ Окончание</th>
                <th style={thStyle}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const st = STATUS_LABELS[u.status] || STATUS_LABELS['not-activated'];
                return (
                  <tr key={u._id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{u.email}</div>
                      {u.name && <div style={{ fontSize: 11, color: '#94a3b8' }}>{u.name}</div>}
                    </td>
                    <td style={tdStyle}>{u.company || '—'}</td>
                    <td style={tdStyle}>{fmtDate(u.promoEmailSentAt)}</td>
                    <td style={{ ...tdStyle, color: u.promoLinkClickedAt ? '#f59e0b' : '#cbd5e1' }}>{fmtDate(u.promoLinkClickedAt)}</td>
                    <td style={{ ...tdStyle, color: u.promoActivatedAt ? '#10b981' : '#cbd5e1' }}>{fmtDate(u.promoActivatedAt)}</td>
                    <td style={{ ...tdStyle, color: u.promoEndsAt && new Date(u.promoEndsAt) < new Date() ? '#ef4444' : '#0f172a' }}>{fmtDate(u.promoEndsAt)}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '4px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700, color: st.color, background: st.bg }}>
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Хелперы ───
const Card: React.FC<{ label: string; value: number; color: string; sub?: string }> = ({ label, value, color, sub }) => (
  <div style={{ padding: 16, background: 'white', borderRadius: 12, borderLeft: `4px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color }}>{value} {sub && <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{sub}</span>}</div>
  </div>
);

const FilterBtn: React.FC<{ active: boolean; onClick: () => void; label: string; count: number }> = ({ active, onClick, label, count }) => (
  <button onClick={onClick} style={{ padding: '8px 14px', borderRadius: 100, border: active ? 'none' : '1px solid #e2e8f0', background: active ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : 'white', color: active ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
    {label} <span style={{ marginLeft: 4, opacity: 0.7 }}>({count})</span>
  </button>
);

const thStyle: React.CSSProperties = { padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 };
const tdStyle: React.CSSProperties = { padding: '12px 14px', color: '#0f172a' };

export default PromoAnalyticsPanel;
